import { useParams, Link } from "react-router-dom";
import { ArrowRight, Phone, MessageCircle, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import BookingForm from "@/components/BookingForm";
import { DynIcon } from "@/components/ui-bits";
import { useService, useServices } from "@/hooks/usePublicData";
import { useSite, telLink, waLink } from "@/context/SiteContext";
import { CITY_LINKS } from "@/data/site";
import NotFound from "@/pages/NotFound";

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = useSite();
  const { data: service, isLoading, isError } = useService(slug);
  const { data: all = [] } = useServices();

  if (isError) return <NotFound />;
  if (isLoading || !service) return <div className="container-x py-24 text-center text-slate-400">Loading…</div>;

  const related = all.filter((x) => x.slug !== slug).slice(0, 6);
  const bullets = [
    "Clean, air-conditioned and well-maintained vehicles",
    "Professional, verified and courteous drivers",
    "Transparent pricing with no hidden charges",
    "Available 24/7 across Jodhpur, Jaipur, Udaipur and Jaisalmer",
  ];

  return (
    <>
      <Seo title={`${service.title} | Chouhan Taxi Service`}
        description={service.short}
        path={`/taxi-services/${slug}`} />
      <Breadcrumbs items={[{ label: "Taxi Services", to: "/taxi-services" }, { label: service.title }]} />
      <section className="section-pad pt-8">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="w-14 h-14 rounded-2xl bg-brand flex items-center justify-center mb-5"><DynIcon name={service.icon} className="w-7 h-7 text-white" /></div>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-navy-heading">{service.title}</h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">{service.description}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {bullets.map((b) => (
                <div key={b} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <CheckCircle2 className="w-5 h-5 text-brand shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700">{b}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/book-taxi" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full hover:bg-royal transition-colors">Book this Service <ArrowRight className="w-4 h-4" /></Link>
              <a href={telLink(s)} className="inline-flex items-center gap-2 border border-slate-300 text-navy font-semibold px-6 py-3 rounded-full hover:border-brand transition-colors"><Phone className="w-4 h-4" /> Call Now</a>
              <a href={waLink(s)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-whatsapp text-white font-semibold px-6 py-3 rounded-full"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>

            <div className="mt-12">
              <h2 className="font-heading font-bold text-navy-heading text-xl mb-4">Available in these cities</h2>
              <div className="flex flex-wrap gap-3">
                {CITY_LINKS.map((c) => (
                  <Link key={c.slug} to={`/${c.slug}-taxi`} className="px-4 py-2 rounded-full bg-brand-light text-brand font-semibold text-sm hover:bg-brand hover:text-white transition-colors">{service.title} in {c.name}</Link>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <h2 className="font-heading font-bold text-navy-heading text-xl mb-4">Other Services</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/taxi-services/${r.slug}`} className="flex items-center gap-2 p-3 rounded-xl border border-slate-100 hover:border-brand transition-colors text-sm font-medium text-navy-heading">
                    <DynIcon name={r.icon} className="w-4 h-4 text-brand" /> {r.title}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-2xl border border-slate-100 shadow-lg p-6">
              <h2 className="font-heading font-bold text-navy-heading text-lg mb-4">Book {service.title}</h2>
              <BookingForm compact defaults={{ trip_type: "One Way", message: `Enquiry for ${service.title}` }} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
